import "./index.css";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { ResourceLabelComponent } from "../ResourceLabelComponent/index-v2";
import { useEffect, useRef, useState, useCallback } from "react";
import { useTheme } from "../../contexts/ThemeContext";
import { darken, readableColor, setLightness } from "polished";
import { Accordion } from "radix-ui";
import React from "react";
import { useAccordionScroll } from "../../hooks/useAccordionScroll";

const LABEL = "label";

export const CourseContentModuleRowComponent = ({ module, moodle, idx, container, navigating, contentFixedBar }) => {
  const theme = useTheme();
  const contentRef = useRef(null);
  const rowRef = useRef(null);

  const newModules = module.modules.filter((m) => m.modname !== LABEL);
  const isUserVisible = module.uservisible;
  const isMobile = typeof window !== "undefined" && window.innerWidth < 1024;

  const isCurrentModule = module.id === moodle.currentModuleId;
  const isCurrentResource = module.modules.some((r) => r.id === moodle.currentResourceId);

  const textColorByContrast = readableColor(
    isCurrentModule ? theme.colors.backgroundColorCurrentModule : theme.colors.backgroundColorNotCurrentModule,
    "#1a1a1a",
    "#f2f2f2",
    false
  );

  const shouldAutoOpen = isCurrentModule || isCurrentResource;
  const [isOpen, setIsOpen] = useState(shouldAutoOpen);
  const manuallyClosedRef = useRef(false);
  const prevCurrentModuleIdRef = useRef(moodle.currentModuleId);

  // Hook para coordinar scroll con Accordion
  const { scheduleScroll, executeScheduledScroll } = useAccordionScroll({
    containerId: 'scrollable-modules-wrapper',
    stickyOffset: contentFixedBar || 0,
    additionalOffset: 0
  });

  // Auto-abrir solo en carga inicial o cuando cambia el módulo activo
  // No reabrir si el usuario cerró manualmente
  useEffect(() => {
    const moduleChanged = prevCurrentModuleIdRef.current !== moodle.currentModuleId;
    prevCurrentModuleIdRef.current = moodle.currentModuleId;

    // Si cambió el módulo activo, resetear el flag de cierre manual
    if (moduleChanged) {
      manuallyClosedRef.current = false;
    }

    // Solo abrir si:
    // 1. Debe estar abierto (es el módulo activo o contiene el recurso activo)
    // 2. No está abierto actualmente
    // 3. El usuario no lo cerró manualmente
    if (shouldAutoOpen && !isOpen && !manuallyClosedRef.current) {
      setIsOpen(true);
      // En mobile se mantiene el scroll al header del módulo (comportamiento actual).
      // En desktop NO se agenda: el scroll lo resuelve ResourceLabelComponent sobre
      // la actividad seleccionada para evitar saltar a la primera actividad del módulo.
      if (isCurrentModule && isMobile) {
        scheduleScroll(rowRef.current);
      }
    }
  }, [shouldAutoOpen, isOpen, isCurrentModule, scheduleScroll, moodle.currentModuleId, isMobile]);

  /**
   * Handler cuando el usuario hace clic en el trigger del Accordion
   * - Al abrir cualquier módulo: scroll a su header
   * - Al cerrar: marcar como cerrado manualmente si es el módulo activo
   */
  const handleValueChange = useCallback((value) => {
    const willOpen = value === "item-1";
    setIsOpen(willOpen);
    
    // Si el usuario cierra el módulo activo, marcar como cerrado manualmente
    if (!willOpen && isCurrentModule) {
      manuallyClosedRef.current = true;
    }
    
    // Programar scroll al header cuando se abre cualquier módulo
    if (willOpen) {
      scheduleScroll(rowRef.current);
    }
  }, [scheduleScroll, isCurrentModule]);

  /**
   * Handler cuando termina la animación del Accordion
   * - Al abrir cualquier módulo: scroll a su header
   * - Al cerrar módulo activo: scroll al módulo activo
   * - Al cerrar módulo no activo: scroll al módulo activo
   */
  const handleAccordionAnimationEnd = useCallback(() => {
    if (isOpen) {
      // Cualquier módulo se abrió → ejecutar scroll programado a su header
      executeScheduledScroll();
    } else {
      // Módulo se cerró
      // En mobile se mantiene el scroll al módulo activo (comportamiento actual).
      // En desktop NO se agenda: evita el salto a la primera actividad del módulo
      // cuando Radix Accordion type="single" fuerza el cierre del módulo anterior
      // durante una navegación. El scroll a la actividad seleccionada lo resuelve
      // ResourceLabelComponent.
      if (isMobile) {
        if (isCurrentModule) {
          // Módulo activo se cerró → scroll al módulo activo actual
          const activeModule = document.getElementById("current-active-module");
          if (activeModule) {
            scheduleScroll(activeModule);
            executeScheduledScroll();
          }
        } else {
          // Módulo no activo se cerró → scroll al módulo activo
          const activeModule = document.getElementById("current-active-module");
          if (activeModule) {
            scheduleScroll(activeModule);
            executeScheduledScroll();
          }
        }
      }
    }
  }, [isOpen, isCurrentModule, scheduleScroll, executeScheduledScroll, isMobile]);

  return (
    <Accordion.Root
      ref={rowRef}
      className={`AccordionRoot wrapper-row ${isCurrentResource ? "current-resource-container" : ""}`}
      type="single"
      value={isOpen ? "item-1" : ""}
      onValueChange={handleValueChange}
      collapsible
      data-module-id={module.id}
      data-contains-resource-id={isCurrentResource ? moodle.currentResourceId : ""}
      style={{
        opacity: 1,
        pointerEvents: !isUserVisible ? "none" : "unset",
        display: !isUserVisible ? "none" : "block",
      }}
    >
      <Accordion.Item className={`AccordionItem ${isCurrentModule ? "module-border-active" : "module-border"}`} value="item-1">
        {/* HEADER → Trigger */}
        <AccordionTrigger asChild>
          <div
            className={`row-container rounded-t-xl module-header`}
            id={isCurrentModule ? "current-active-module" : ""}
            style={{
              backgroundColor: isCurrentModule
                ? theme.colors.backgroundColorCurrentModule
                : theme.mode === "light"
                  ? setLightness(0.9, theme.colors.backgroundColorNotCurrentModule)
                  : theme.colors.backgroundColorNotCurrentModule,
            }}
          >
            {/* ÍNDICE */}
            <div className="pl-3 flex justify-center items-center">
              <div
                className="opacity-85 rounded-md w-10 h-10 flex justify-center items-center border-opacity-15 border-[.5px] border-gray-200"
                style={{
                  backgroundColor: theme.colors.backgroundColorIndexRow,
                }}
              >
                <p
                  className="text-2xl font-outfit font-semibold"
                  style={{
                    color: readableColor(darken(0.09, theme.colors.backgroundColorIndexRow)),
                  }}
                >
                  {idx + 1}
                </p>
              </div>
            </div>

            {/* TÍTULO + PROGRESO */}
            <div className="title-container grid gap-2" style={{ width: "100%" }}>
              <div
                className="title-btn font-outfit text-2xl font-medium"
                style={{
                  color: isCurrentModule ? textColorByContrast : theme.colors.textColorDefault,
                }}
              >
                {module.name}
              </div>
              {module.hasProgress && <ProgressBar progress={module.rawProgress} theme={theme} isCurrentModule={isCurrentModule} />}
              {module.availabilityinfo && (
                <p
                  style={{ fontSize: 10, marginTop: 2, opacity: 1 }}
                  dangerouslySetInnerHTML={{
                    __html: module.availabilityinfo,
                  }}
                />
              )}
            </div>

            {/* ICONO */}
            {newModules?.length > 0 && (
              <div className={`AccordionChevronWrapper`}>
                <KeyboardArrowRightIcon
                  sx={{
                    color: isCurrentModule ? textColorByContrast : theme.colors.textColorDefault,
                    width: 20,
                    height: 20,
                  }}
                />
              </div>
            )}
          </div>
        </AccordionTrigger>

        {/* CONTENT */}
        <Accordion.Content
          onAnimationEnd={handleAccordionAnimationEnd}
          ref={contentRef}
          className="module-content rounded-b-xl AccordionContent module-content-body"
          style={{
            // paddingLeft: "26px",
            borderLeftWidth: ".1px",
            borderRightWidth: ".1px",
            borderBottomWidth: ".1px",
            borderColor: isCurrentModule
              ? theme.mode === "light"
                ? theme.colors.backgroundColorCurrentModule
                : theme.colors.borderColorCurrentModule + "88"
              : "transparent",
          }}
        >
          <div className="AccordionContentInner">
            {newModules &&
              newModules.map((resource, idx) => {
                return (
                  <ResourceLabelComponent
                    container={container}
                    module={module}
                    resource={resource}
                    key={resource.id}
                    moodle={moodle}
                    idx={idx}
                    isCurrentModule={isCurrentModule}
                    isModuleOpen={isOpen}
                    contentFixedBar={contentFixedBar}
                  />
                );
              })}
          </div>
        </Accordion.Content>
      </Accordion.Item>
    </Accordion.Root>
  );
};

const AccordionTrigger = React.forwardRef(({ children, ...props }, ref) => (
  <Accordion.Header>
    <Accordion.Trigger {...props} ref={ref}>
      {children}
    </Accordion.Trigger>
  </Accordion.Header>
));

const ProgressBar = ({ progress, theme, isCurrentModule, container }) => {
  const [color, setColor] = useState("");

  useEffect(() => {
    if (theme.colors) {
      setColor(
        isCurrentModule
          ? readableColor(theme.colors.backgroundColorCurrentModule)
          : theme.mode === "light"
          ? readableColor(setLightness(0.9, theme.colors.backgroundColorNotCurrentModule))
          : readableColor(setLightness(0.15, theme.colors.backgroundColorNotCurrentModule))
      );
    }
  }, [theme.colors, isCurrentModule]);

  let width = "100";
  let progressWidth = (progress * width) / 100;

  return (
    <div
      className="title-container "
      style={{
        // transform: "translateY(10px)",
        width: "100%",
        ...container,
      }}
    >
      <div
        style={{
          backgroundColor: "#fff",
          width: `${width}%`,
          height: 5,
          borderRadius: "5px",
        }}
      >
        <div
          style={{
            backgroundColor: theme.mode === "light" ? theme.colors.button_light : theme.colors.button_dark,
            width: `${progressWidth}%`,
            height: 5,
            borderRadius: "5px",
          }}
        ></div>
      </div>
      <span
        style={{
          color: color,
          fontSize: "10px",
          marginTop: "5px",
        }}
      >
        {progress}% completado
      </span>
    </div>
  );
};
